import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { onboardingApi } from '../../api/onboarding';
import { genericDocumentApi } from '../../api/generic-documents';
import { StatusBadge } from '../../components/StatusBadge';
import { DataTable } from '../../components/DataTable';
import { CommentsThread } from '../../components/CommentsThread';
import { DocumentsSection } from '../../components/DocumentsSection';
import { RecordHeader } from '../../components/RecordHeader';
import { WorkflowTrackingTab } from '../../components/WorkflowTrackingTab';
import { WorkspaceTabs } from '../../components/WorkspaceTabs';
import { usePermission } from '../../auth/AuthContext';

export function OnboardingDetailPage() {
  const { employeeId } = useParams<{ employeeId: string }>();
  const empId = Number(employeeId);
  const canManage = usePermission('onboarding.manage');
  const detailQuery = useQuery({ queryKey: ['onboarding-detail', empId], queryFn: () => onboardingApi.getForEmployee(empId) });
  const checklistId = detailQuery.data?.data?.id;
  const docsQuery = useQuery({
    queryKey: ['onboarding-documents', checklistId],
    queryFn: () => genericDocumentApi.listFor('onboarding_checklist', checklistId!),
    enabled: !!checklistId,
  });

  if (!detailQuery.data?.data) return <div className="page"><p className="muted">Loading…</p></div>;
  const { data: checklist, tasks } = detailQuery.data;

  return (
    <div className="page">
      <Link to="/hr/onboarding">&larr; Back to Onboarding</Link>
      <RecordHeader
        title={`Onboarding - ${checklist.employee_name ?? ''}`}
        readOnly={!canManage}
        entityType="onboarding_checklist"
        entityId={checklist.id}
        fields={[
          { label: 'Status', value: <StatusBadge status={checklist.status} label={checklist.status === 'completed' ? 'Completed' : 'In Progress'} /> },
          { label: 'Progress', value: `${checklist.completed_tasks ?? 0} / ${checklist.total_tasks ?? 0} tasks` },
        ]}
      />

      <WorkspaceTabs
        tabs={[
          {
            key: 'tasks',
            label: 'Tasks',
            content: (
              <>
                <section className="card">
                  <DataTable
                    rows={tasks}
                    rowKey={(t) => t.id}
                    emptyMessage="No tasks."
                    columns={[
                      { header: 'Category', render: (t) => t.category },
                      { header: 'Task', render: (t) => t.task_name },
                      { header: 'Responsible', render: (t) => t.responsible_name ?? '-' },
                      { header: 'Due', render: (t) => t.due_date ?? '-' },
                      { header: 'Status', render: (t) => <StatusBadge status={t.status} /> },
                      { header: 'Notes', render: (t) => t.notes ?? '-' },
                    ]}
                  />
                </section>
                <CommentsThread entityType="onboarding_checklist" entityId={checklist.id} />
              </>
            ),
          },
          {
            key: 'documents',
            label: 'Documents',
            content: (
              <DocumentsSection
                documents={docsQuery.data?.data ?? []}
                downloadUrl={(documentId) => genericDocumentApi.downloadUrl('onboarding_checklist', documentId)}
                upload={(input) => genericDocumentApi.uploadFor('onboarding_checklist', checklist.id, input)}
                onDelete={(documentId) => genericDocumentApi.deleteFor('onboarding_checklist', documentId)}
                queryKeyToInvalidate={['onboarding-documents', checklist.id]}
                hrCategories={false}
              />
            ),
          },
          {
            key: 'tracking',
            label: 'Tracking',
            content: <WorkflowTrackingTab entityType="onboarding_checklist" entityId={checklist.id} />,
          },
        ]}
      />
    </div>
  );
}
